import axios, {AxiosResponse} from 'axios';
import * as Sentry from '@sentry/react';
import each from 'lodash/each';
import {CLOUDINARY_CDN_HOSTNAME, CLOUDINARY_URL} from '../constants';
import {localiseHostName} from './host-utils';

const imageOptionsMap: Record<string, string> = {
  width: 'w',
  height: 'h',
  pixelRatio: 'dpr',
  crop: 'c'
};

export const CROP = {
  FIT: 'fit',
  LIMIT: 'limit'
};

export const getCloudinaryCdnHostName = () => localiseHostName(CLOUDINARY_CDN_HOSTNAME);

export const getCloudinaryApiUrl = () => localiseHostName(CLOUDINARY_URL);

export const getImageOptions = (overrideOptions?: Record<string, string | number>): string[] => {
  if (!overrideOptions) {
    return [];
  }

  const options = {
    pixelRatio: 'auto',
    crop: CROP.FIT,
    ...overrideOptions
  };

  const optionsArray: string[] = [];

  each(options, (option, key) => {
    optionsArray.push(`${imageOptionsMap[key]}_${option}`);
  });

  return optionsArray;
};

export const CHUNK_SIZE_BYTES = 20 * 1024 * 1024;

export interface CloudinaryUploadParams {
  folder: string;
  uploadPreset: string;
  timestamp?: number;
  cancelToken?: ReturnType<typeof axios.CancelToken.source>;
  onUploadProgress?: (progressEvent: ProgressEvent) => void;
}

export interface CloudinaryUploadResponse {
  secure_url: string;
  public_id: string;
  resource_type: string;
  [key: string]: unknown;
}

const buildFormData = (filePart: Blob, params: CloudinaryUploadParams, fileName?: string): FormData => {
  const formData = new FormData();
  if (fileName) {
    formData.append('file', filePart, fileName);
  } else {
    formData.append('file', filePart);
  }
  formData.append('upload_preset', params.uploadPreset);
  formData.append('folder', params.folder);
  formData.append('timestamp', String(params.timestamp));
  formData.append('resource_type', 'auto');
  formData.append('quality', 'auto');
  return formData;
};

// Not a security token — only groups the chunks of one upload, so Math.random is safe here.
const generateUniqueUploadId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const uploadFileToCloudinary = async (
  file: File,
  params: CloudinaryUploadParams
): Promise<CloudinaryUploadResponse> => {
  try {
    return await performUpload(file, params);
  } catch (error) {
    const cloudinaryMessage = (error as {response?: {data?: {error?: {message?: string}}}})?.response?.data?.error
      ?.message;
    if (cloudinaryMessage) {
      Sentry.captureMessage(`Cloudinary rejected the upload: ${cloudinaryMessage}`, {
        level: 'error',
        extra: {
          uploadPreset: params.uploadPreset,
          folder: params.folder,
          fileName: file.name,
          fileSizeBytes: file.size
        }
      });
    }
    throw error;
  }
};

const performUpload = async (file: File, params: CloudinaryUploadParams): Promise<CloudinaryUploadResponse> => {
  const url = getCloudinaryApiUrl();
  const {cancelToken, onUploadProgress} = params;

  if (file.size <= CHUNK_SIZE_BYTES) {
    const {data} = await axios.post(url, buildFormData(file, params), {
      cancelToken: cancelToken?.token,
      onUploadProgress: onUploadProgress
    });
    return data;
  }

  const uniqueUploadId = generateUniqueUploadId();
  let response: AxiosResponse<any>;

  for (let start = 0; start < file.size; start += CHUNK_SIZE_BYTES) {
    const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
    const chunk = file.slice(start, end);

    const onChunkProgress = (progressEvent: ProgressEvent) => {
      if (onUploadProgress) {
        const processedChunkBytes = start + progressEvent.loaded;
        const totalProgressEvent = {
          ...progressEvent,
          loaded: processedChunkBytes,
          total: file.size
        };
        onUploadProgress(totalProgressEvent);
      }
    };

    response = await axios.post(url, buildFormData(chunk, params, file.name), {
      headers: {
        'X-Unique-Upload-Id': uniqueUploadId,
        'Content-Range': `bytes ${start}-${end - 1}/${file.size}`
      },
      cancelToken: cancelToken?.token,
      onUploadProgress: onChunkProgress
    });
  }

  return response!.data;
};
